import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useRecaptcha } from "@/lib/useRecaptcha";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const { execute } = useRecaptcha();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("This reset link is missing its token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (password !== confirm) { toast.error("Passwords do not match."); return; }
    setBusy(true);
    try {
      const captcha = await execute("reset_password");
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password, recaptcha_token: captcha });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reset password. The link may have expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      <div className="flex flex-col px-6 sm:px-10 lg:px-16 py-8 lg:py-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-[#0A2342] flex items-center justify-center">
            <span className="text-[#4FD1C5] font-heading font-bold text-sm">D</span>
          </div>
          <span className="font-heading font-bold text-[15px] text-[#0A2342]">Dive Media Strategist&trade;</span>
        </Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md">
            {done ? (
              <div data-testid="reset-password-success">
                <div className="w-12 h-12 rounded-full bg-[#4FD1C5]/15 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-6 h-6 text-[#0F766E]" />
                </div>
                <div className="dms-label mb-3">All set</div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-3">Password updated.</h1>
                <p className="text-[#475569] leading-relaxed">
                  Redirecting you to sign in with your new password. For your security, every device that was signed in has been logged out.
                </p>
                <Link to="/login" data-testid="reset-password-to-login" className="mt-6 inline-flex text-sm font-semibold text-[#1D4E89] hover:text-[#0A2342]">
                  Take me to sign in &rarr;
                </Link>
              </div>
            ) : !token ? (
              <div>
                <div className="w-12 h-12 rounded-full bg-[#DC2626]/10 flex items-center justify-center mb-5">
                  <AlertTriangle className="w-6 h-6 text-[#DC2626]" />
                </div>
                <div className="dms-label mb-3">Invalid link</div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-3">This link is missing its token.</h1>
                <p className="text-[#475569] leading-relaxed">
                  Reset links must be opened exactly as they appear in your email. Request a fresh one and try again.
                </p>
                <Link to="/forgot-password" className="mt-6 inline-flex dms-btn-primary px-5 py-2.5">Request a new link</Link>
              </div>
            ) : (
              <div>
                <div className="w-12 h-12 rounded-full bg-[#0A2342]/6 flex items-center justify-center mb-5">
                  <Lock className="w-6 h-6 text-[#0A2342]" />
                </div>
                <div className="dms-label mb-3">Reset password</div>
                <h1 className="text-3xl sm:text-4xl font-heading font-bold text-[#0A2342] mb-2">Set a new password.</h1>
                <p className="text-[#475569] mb-8">At least 8 characters. Choose something you&apos;ll actually remember.</p>

                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <label className="dms-label block mb-2">New password</label>
                    <input
                      data-testid="reset-password-new"
                      required
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="dms-input"
                      placeholder="At least 8 characters"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="dms-label block mb-2">Confirm new password</label>
                    <input
                      data-testid="reset-password-confirm"
                      required
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="dms-input"
                      placeholder="Type it again"
                    />
                  </div>
                  <button data-testid="reset-password-submit" type="submit" disabled={busy} className="w-full dms-btn-primary">
                    {busy ? "Saving…" : "Reset password"}
                  </button>
                </form>
                <p className="mt-4 text-[13.5px] text-[#64748B]">
                  Once you reset, every device signed into this account will be logged out — you&apos;ll sign back in with the new password.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1602199926649-2e5e447bab97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxzY3ViYSUyMGRpdmVyJTIwdW5kZXJ3YXRlciUyMHBob3RvZ3JhcGh5fGVufDB8fHx8MTc4MTQ2MTM0MHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A2342]/60 via-transparent to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#4FD1C5]">Doctrine</div>
          <p className="mt-3 text-xl font-heading font-semibold leading-snug">A strong password is the first tool in your kit.</p>
        </div>
      </div>
    </div>
  );
}
